import type { ContributionTaskKey } from "./task-registry";

type Localized = { fr: string; en: string };

/**
 * Rules of the "Consignes" dialog, worded for what the person does on that
 * task in this app (button names match the action bar of each page).
 */
export const TASK_GUIDELINES: Record<ContributionTaskKey, Localized[]> = {
  speak: [
    { fr: "Lisez la phrase en entier avant d'enregistrer.", en: "Read the whole sentence before recording." },
    { fr: "Parlez clairement, dans un endroit calme, à votre rythme.", en: "Speak clearly, in a quiet place, at your own pace." },
    { fr: "Prononcez la phrase telle qu'elle est écrite, sans ajouter ni retirer de mots.", en: "Say the sentence as written, without adding or dropping words." },
    { fr: "Réécoutez votre enregistrement avant de l'envoyer, et refaites-le si besoin.", en: "Listen back before sending, and record again if needed." },
    { fr: "Si la phrase est incorrecte, passez à la suivante ou utilisez « Signaler ».", en: "If the sentence is wrong, move to the next one or use \"Report\"." },
  ],
  verifyAudio: [
    { fr: "Écoutez l'enregistrement en entier avant de décider.", en: "Listen to the whole recording before deciding." },
    { fr: "Indiquez la voix entendue (femme ou homme) : elle est demandée pour valider.", en: "Pick the voice you hear (female or male): it is required to accept." },
    { fr: "Validez si chaque mot correspond à la phrase affichée.", en: "Accept if every word matches the displayed sentence." },
    { fr: "Rejetez si un mot manque, est ajouté ou déformé, en choisissant un motif.", en: "Reject if a word is missing, added or distorted, and pick a reason." },
    { fr: "Un léger bruit de fond est acceptable tant que la voix reste claire.", en: "Light background noise is fine as long as the voice stays clear." },
  ],
  transcribe: [
    { fr: "Écoutez l'enregistrement en entier, autant de fois que nécessaire.", en: "Listen to the whole recording, as many times as needed." },
    { fr: "Écrivez exactement ce que vous entendez, sans corriger ni reformuler.", en: "Write exactly what you hear, without correcting or rephrasing." },
    { fr: "Gardez la ponctuation et les caractères spéciaux de la langue.", en: "Keep the punctuation and special characters of the language." },
    { fr: "Si l'audio est inaudible ou dans une autre langue, utilisez « Signaler ».", en: "If the audio is inaudible or in another language, use \"Report\"." },
  ],
  translate: [
    { fr: "Lisez la phrase en entier avant de la traduire.", en: "Read the whole sentence before translating it." },
    { fr: "Traduisez le sens, avec des mots naturels dans votre langue.", en: "Translate the meaning, with natural words in your language." },
    { fr: "Utilisez la ponctuation et les caractères spéciaux de la langue.", en: "Use the punctuation and special characters of the language." },
    { fr: "Si la phrase est incorrecte ou illisible, utilisez « Signaler ».", en: "If the sentence is wrong or unreadable, use \"Report\"." },
  ],
  verifyText: [
    { fr: "Lisez la transcription, puis écoutez l'audio en entier.", en: "Read the transcription, then listen to the whole audio." },
    { fr: "Validez si le texte correspond exactement à ce qui est dit.", en: "Accept if the text matches exactly what is said." },
    { fr: "Rejetez si un mot manque, est ajouté ou mal écrit.", en: "Reject if a word is missing, added or misspelled." },
    { fr: "Ne jugez pas la voix ni la qualité du son, seulement le texte.", en: "Do not judge the voice or sound quality, only the text." },
  ],
  images: [
    { fr: "Regardez l'image en entier avant de répondre.", en: "Look at the whole image before answering." },
    { fr: "Écrivez exactement les caractères que vous reconnaissez.", en: "Type exactly the characters you recognise." },
    { fr: "Si l'image est illisible ou vide, utilisez « Signaler ».", en: "If the image is unreadable or empty, use \"Report\"." },
  ],
  answerAudio: [
    { fr: "Lisez la question en entier avant d'enregistrer.", en: "Read the whole question before recording." },
    { fr: "Répondez librement dans votre langue, d'une voix claire.", en: "Answer freely in your language, with a clear voice." },
    { fr: "Réécoutez votre réponse avant de l'envoyer.", en: "Listen back to your answer before sending it." },
    { fr: "Si la question est incorrecte, utilisez « Signaler ».", en: "If the question is wrong, use \"Report\"." },
  ],
  answerText: [
    { fr: "Lisez la question en entier avant de répondre.", en: "Read the whole question before answering." },
    { fr: "Répondez par écrit dans votre langue, avec ses caractères spéciaux.", en: "Answer in writing in your language, with its special characters." },
    { fr: "Relisez votre réponse avant de l'envoyer.", en: "Read your answer again before sending it." },
    { fr: "Si la question est incorrecte, utilisez « Signaler ».", en: "If the question is wrong, use \"Report\"." },
  ],
  transcribeInitial: [
    { fr: "Écoutez la réponse en entier avant d'écrire.", en: "Listen to the whole answer before writing." },
    { fr: "Écrivez exactement ce qui est dit, dans la langue parlée, sans traduire.", en: "Write exactly what is said, in the spoken language, without translating." },
    { fr: "La traduction française est facultative.", en: "The French translation is optional." },
    { fr: "Passez un audio inaudible ou que vous ne comprenez pas.", en: "Skip an audio you cannot hear or understand." },
  ],
  reviewInitial: [
    { fr: "Comparez chaque proposition à l'audio ou à la question posée.", en: "Compare each proposal with the audio or the question asked." },
    { fr: "Validez une proposition juste ; sinon rejetez-la en donnant un motif.", en: "Accept a correct proposal; otherwise reject it with a reason." },
    { fr: "Corrigez quand vous connaissez le texte juste.", en: "Correct it when you know the right text." },
    { fr: "Vos propres réponses et transcriptions ne vous sont jamais proposées.", en: "You are never offered your own answers or transcriptions." },
  ],
};

const OFFENSIVE: Localized = { fr: "Contenu offensant", en: "Offensive content" };
const OTHER: Localized = { fr: "Autre", en: "Other" };
const WRONG_SENTENCE: Localized = { fr: "Phrase incorrecte ou illisible", en: "Wrong or unreadable sentence" };
const BAD_AUDIO: Localized = { fr: "Audio inaudible ou vide", en: "Inaudible or empty audio" };
const WRONG_LANGUAGE: Localized = { fr: "Mauvaise langue ou dialecte", en: "Wrong language or dialect" };
const WRONG_QUESTION: Localized = { fr: "Question incorrecte ou illisible", en: "Wrong or unreadable question" };

/** Reasons offered in the "Signaler" dialog, per task. The first one is preselected. */
export const TASK_REPORT_REASONS: Record<ContributionTaskKey, Localized[]> = {
  speak: [
    WRONG_SENTENCE,
    { fr: "Phrase dans une autre langue", en: "Sentence in another language" },
    { fr: "Impossible d'enregistrer (micro, son)", en: "Cannot record (microphone, sound)" },
    OFFENSIVE,
    OTHER,
  ],
  verifyAudio: [BAD_AUDIO, WRONG_LANGUAGE, WRONG_SENTENCE, OFFENSIVE, OTHER],
  transcribe: [BAD_AUDIO, WRONG_LANGUAGE, OFFENSIVE, OTHER],
  translate: [WRONG_SENTENCE, { fr: "Phrase dans une autre langue", en: "Sentence in another language" }, OFFENSIVE, OTHER],
  verifyText: [BAD_AUDIO, { fr: "Transcription illisible", en: "Unreadable transcription" }, WRONG_LANGUAGE, OFFENSIVE, OTHER],
  images: [{ fr: "Image illisible ou vide", en: "Unreadable or empty image" }, OFFENSIVE, OTHER],
  answerAudio: [WRONG_QUESTION, { fr: "Impossible d'enregistrer (micro, son)", en: "Cannot record (microphone, sound)" }, OFFENSIVE, OTHER],
  answerText: [WRONG_QUESTION, OFFENSIVE, OTHER],
  transcribeInitial: [BAD_AUDIO, WRONG_LANGUAGE, OFFENSIVE, OTHER],
  reviewInitial: [BAD_AUDIO, { fr: "Proposition illisible", en: "Unreadable proposal" }, WRONG_LANGUAGE, OFFENSIVE, OTHER],
};

export const pickLocalized = (entry: Localized, lang: "fr" | "en") => entry[lang];
